import type { SnapshotFrom } from 'xstate';
import { getMachine } from './machine';
import type { MultiStepDynamicForm } from '@/store/form-types';
import type { FormModes } from '@/store/modules/assignments/assignments-types';
import type { User } from '@/store/modules/auth/auth-types';
import type { FolderFileStructure, IFile } from '@/interfaces/FileManager';
import type {
  AccountMappingConnectionAction,
  RowSelectionConfiguration,
  Target,
  TargetValueProposal,
} from '@/store/modules/account-mapping/account-mapping-types';
import type { Workbook } from '@syncfusion/ej2-spreadsheet';

export type MachineEvents =
  | { type: 'UPDATE_ASSIGNMENT_TYPE'; value: string }
  | { type: 'UPDATE_TAXONOMY'; value: string }
  | { type: 'UPDATE_ASSIGNMENT_NAME'; value: string }
  | { type: 'UPDATE_BALANCE_SHEET_DATE'; value: string }
  | { type: 'NEXT' }
  | { type: 'TOGGLE_USE_TEMPLATE'; value: boolean }
  | { type: 'TOGGLE_SUBSEQUENT_PERIOD'; value: boolean }
  | { type: 'SELECT_TEMPLATE'; value: string }
  | { type: 'BACK' }
  | { type: 'UPDATE_USERS'; value: UserOption[] }
  | { type: 'INVITE_USER'; value: string }
  | { type: 'UPDATE_CHECKLIST'; value: string }
  | { type: 'UPLOAD_FILE'; value: File }
  | { type: 'UPLOAD_TARGETS_FILE'; value: File }
  | { type: 'DELETE_TARGETS_FILE' }
  | { type: 'UPDATE_TARGET'; value: { targetTaxonomy: Target; year: 'current' | 'previous' } }
  | { type: 'EXPAND_TARGET'; value: { id: string; expanded?: boolean } }
  | { type: 'SAVE_TARGET'; value: { targetTaxonomy: Target; year: 'current' | 'previous' } }
  | { type: 'APPLY_TARGET_PROPOSALS'; value: TargetValueProposal[] }
  | { type: 'DELETE_TARGET_VALUES' }
  | { type: 'UPLOAD_ACCOUNTS_FILE'; value: File }
  | { type: 'UPDATE_FIELD'; value: { key: string; value: any } }
  | { type: 'DELETE_ACCOUNTS_FILE' }
  | { type: 'UPDATE_TOGGLE_ALL_VALUE'; value: boolean }
  | { type: 'UPDATE_SPREADSHEET_JSON'; value: { jsonObject: { Workbook: Workbook } } }
  | { type: 'UPDATE_SELECTED_ROWS'; value: number[] }
  | { type: 'SAVE_SPREADSHEET'; value: { jsonObject: { Workbook: Workbook } } }
  | { type: 'UPDATE_COA_META'; value: CoaMeta }
  | { type: 'RESET_GENERAL_ERRORS' }
  | { type: 'FINISH' };

export type MachineSnapshot = SnapshotFrom<ReturnType<typeof getMachine>>;

export const AssignmentTypes = {
  notes: 'notes',
  managementReport: 'management-report',
  sustainabilityReport: 'sustainability-report',
  accountMapping: 'account-mapping',
  numericalAudit: 'numerical-audit',
} as const;

export type AssignmentType = (typeof AssignmentTypes)[keyof typeof AssignmentTypes];

export type ChecklistOption = {
  assignmentType: AssignmentType;
  displayName: string;
  name: string;
};

export type Step1Fields = {
  assignmentType: AssignmentType;
  assignmentName: string;
  balanceSheetDate: string;
};

export type Step2Fields = {
  useTemplate: boolean;
  isSubsequentPeriod: boolean;
  templateAssignmentId?: string;
};

export type Step3Fields = {
  users: UserOption[];
  checklist: string;
  uploadedFile?: File;
  pdfId: string;
  taxonomy: string;
};

export type Step4Fields = {
  accountsFile?: File;
  accountsFileDetails?: IFile;
  spreadSheetJson?: { jsonObject: { Workbook: Workbook } };
};

export type DynamicForm = MultiStepDynamicForm & {
  values: any; // form values are written into this object
};

export type DynamicFormFields = {
  dynamicFormIndex: number;
  dynamicForms: DynamicForm[];
};

export type FormFieldType = Partial<Step1Fields & Step2Fields & Step3Fields & Step4Fields>;

export type FormErrorStructure = keyof FormFieldType;

export type CoaMeta = {
  selectedSheet?: number;
  columnMapping?: {
    accountNumber: number;
    displayName: number;
    valueCurrentYear: number;
    valuePreviousYear: number;
  };
  selectedRows: number[];
};

export type MachineContext = {
  assignmentId: string;
  companyId: string;
  accountsFileId?: string;
  flow: FormModes;
  canChangeUsers?: boolean;
  assignmentTypeOptions: string[];
  templateOptions: TemplateAssignment[];
  currentUser?: User | null;
  userOptions: UserOption[];
  checklistOptions: ChecklistOption[];
  taxonomyOptions: Taxonomy[];
  taxonomy?: string;
  form3IsSubmitted?: boolean; // used to track if account mapping is converted to draft (by submitting form 3)
  coaMeta: CoaMeta;
  /** Filled as a result of calling /unmapped-accounts/preview-rows BE endpoint
   * in order to prefill configuration for the row selection */
  coaRowConfig?: RowSelectionConfiguration;
  errors?: { [key in FormErrorStructure]?: string } & { general?: string };
  warnings?: { [key in FormErrorStructure]?: string };

  targetImports?: TargetsImportContext;
} & Partial<Step1Fields> &
  Partial<Step2Fields> &
  Partial<Step3Fields> &
  Partial<Step4Fields> &
  Partial<DynamicFormFields>;

export type UserOption = {
  id: string;
  email: string;
  name: string;
  verified: boolean;
  value?: string;
  selected?: boolean;
};

export type TargetsImportContext = {
  pending?: string;
  assignmentId: string;
  companyId: string;
  coaTree?: Target[];
  pendingChanges: AccountMappingConnectionAction[];
  jobId?: string;
  fileId?: string;
  file?: File;
  fileDetails?: IFile;
  errors?: { [key in FormErrorStructure]?: string };
  warnings?: { [key in FormErrorStructure]?: string };
  flow: FormModes;
};

export type TemplateAssignment = {
  assignmentId: string;
  assignmentName: string;
  assignmentType: AssignmentType;
  checklist: {
    assignmentType: AssignmentType;
    displayName: string;
    name: string;
  };
  taxonomy: Taxonomy;
  companyId: string;
  companyName: string;
  companyNumber: string;
};

export type InitResponse = {
  assignmentTypes: string[];
  checklists: ChecklistOption[];
  taxonomies: Taxonomy[];
  prefilledData: Partial<
    Step1Fields & {
      checklist?: ChecklistOption;
      usersWithEditRights?: UserOption[];
      taxonomy?: Taxonomy;
      isSubsequentPeriod?: boolean;
      templateAssignmentId?: string;
    }
  >;
  templateAssignments: TemplateAssignment[];
  users: UserOption[];
};

export type UploadFileResponse = {
  data: {
    pdfId: string;
    warningData: any;
  };
  errorData: {
    errorCode: string;
  };
  success: boolean;
};

export type InviteUserResponse = {
  data: {
    assignmentId: string;
    assignmentUsersForm: {
      key: string;
      items: (UserOption & {
        disabled: boolean;
        selected: boolean;
        value: string;
      })[];
    }[];
  };
  errorData: {
    errorCode: string;
  };
  success: boolean;
};

export type ServerValidationResult = {
  success: boolean;
  formInputErrors?: { [key: string]: string };
};

export interface Taxonomy {
  assignmentType: string;
  displayName: string;
  name: string;
}

export interface FetchFileManagerResponse {
  success: boolean;
  fileManagerData: FolderFileStructure;
  isAssignmentPending: boolean;
}

export type ApiActorResponse<T> = {
  success: boolean;
  data?: T;
  formInputErrors?: any;
  errorData?: any;
};
